/* Collection page: AJAX filters + progressive fallback */
(() => {
  const isCollectionPage = () => Boolean(document.querySelector("[data-collection-enhanced]"));

  const parseSectionFromHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let incomingRoot = doc.querySelector("[data-collection-enhanced]");
    if (incomingRoot) return incomingRoot;

    const temp = document.createElement("div");
    temp.innerHTML = html;
    incomingRoot = temp.querySelector("[data-collection-enhanced]");
    return incomingRoot;
  };

  const urlWithSection = (urlLike, sectionId) => {
    const url = new URL(urlLike, window.location.origin);
    if (sectionId) url.searchParams.set("section_id", sectionId);
    return url;
  };

  const buildFormUrl = (form) => {
    const action = form.getAttribute("action") || window.location.pathname;
    const url = new URL(action, window.location.origin);
    const data = new FormData(form);
    data.forEach((value, key) => {
      const text = String(value || "").trim();
      if (text !== "") url.searchParams.append(key, text);
    });
    return url;
  };

  const syncFormStateFromUrl = (form) => {
    const params = new URLSearchParams(window.location.search);

    form.querySelectorAll('input[type="checkbox"]').forEach((input) => {
      const selected = params.getAll(input.name);
      input.checked = selected.includes(input.value);
    });

    form.querySelectorAll('input[type="number"]').forEach((input) => {
      const value = params.get(input.name);
      if (value !== null) input.value = value;
    });

    form.querySelectorAll('input[type="radio"]').forEach((input) => {
      const value = params.get(input.name);
      if (value !== null) input.checked = input.value === value;
    });
  };

  const revealNow = (scope) => {
    scope?.querySelectorAll(".reveal").forEach((el) => el.classList.add("is-visible"));
  };

  const wirePriceRange = (form, onApply) => {
    const minInput = form.querySelector("[data-price-min-input]");
    const maxInput = form.querySelector("[data-price-max-input]");
    const minRange = form.querySelector("[data-price-min-range]");
    const maxRange = form.querySelector("[data-price-max-range]");
    if (!minInput || !maxInput || !minRange || !maxRange) return;

    const clampPair = (minRaw, maxRaw) => {
      const floor = Number.parseInt(minRange.min || "0", 10) || 0;
      const ceil = Number.parseInt(minRange.max || "0", 10) || 0;
      let min = Number.isFinite(minRaw) ? minRaw : floor;
      let max = Number.isFinite(maxRaw) ? maxRaw : ceil;
      min = Math.max(floor, Math.min(min, ceil));
      max = Math.max(floor, Math.min(max, ceil));
      if (min > max) min = max;
      return { min, max };
    };

    const updateVisual = () => {
      const floor = Number.parseInt(minRange.min || "0", 10) || 0;
      const ceil = Number.parseInt(minRange.max || "1", 10) || 1;
      const spread = Math.max(1, ceil - floor);
      const min = Number.parseInt(minRange.value || "0", 10) || 0;
      const max = Number.parseInt(maxRange.value || "0", 10) || 0;
      const minPct = ((min - floor) / spread) * 100;
      const maxPct = ((max - floor) / spread) * 100;
      const wrap = form.querySelector("[data-price-range-wrap]");
      if (!wrap) return;
      wrap.style.setProperty("--range-min", `${Math.max(0, Math.min(100, minPct))}%`);
      wrap.style.setProperty("--range-max", `${Math.max(0, Math.min(100, maxPct))}%`);
    };

    const applyFromRange = (source) => {
      let min = Number.parseInt(minRange.value || "0", 10);
      let max = Number.parseInt(maxRange.value || "0", 10);
      if (source === "min" && min > max) max = min;
      if (source === "max" && max < min) min = max;
      const pair = clampPair(min, max);
      minRange.value = String(pair.min);
      maxRange.value = String(pair.max);
      minInput.value = String(pair.min);
      maxInput.value = String(pair.max);
      updateVisual();
    };

    const applyFromInput = () => {
      const min = Number.parseInt(minInput.value || "0", 10);
      const max = Number.parseInt(maxInput.value || "0", 10);
      const pair = clampPair(min, max);
      minInput.value = String(pair.min);
      maxInput.value = String(pair.max);
      minRange.value = String(pair.min);
      maxRange.value = String(pair.max);
      updateVisual();
    };

    minRange.addEventListener("input", () => applyFromRange("min"));
    maxRange.addEventListener("input", () => applyFromRange("max"));
    minRange.addEventListener("change", onApply);
    maxRange.addEventListener("change", onApply);
    minInput.addEventListener("change", () => {
      applyFromInput();
      onApply();
    });
    maxInput.addEventListener("change", () => {
      applyFromInput();
      onApply();
    });

    applyFromInput();
  };

  const wirePriceBuckets = (form, onApply) => {
    const gteInput = form.querySelector("[data-price-gte-input]");
    const lteInput = form.querySelector("[data-price-lte-input]");
    const radios = Array.from(form.querySelectorAll("[data-price-bucket]"));
    if (!gteInput || !lteInput || !radios.length) return;

    const applyBucket = (radio) => {
      if (!radio) return;
      const gte = radio.getAttribute("data-price-gte") || "";
      const lte = radio.getAttribute("data-price-lte") || "";
      gteInput.value = gte;
      lteInput.value = lte;
    };

    const checked = radios.find((radio) => radio.checked) || radios[0];
    if (checked) applyBucket(checked);

    radios.forEach((radio) => {
      radio.addEventListener("change", () => {
        if (!radio.checked) return;
        applyBucket(radio);
        onApply();
      });
    });
  };

  const initCollection = (root) => {
    if (!root || root.dataset.collectionAjaxBound === "true") return;
    root.dataset.collectionAjaxBound = "true";
    revealNow(root);

    const form = root.querySelector("[data-collection-filter-form]");
    if (!form) return;
    syncFormStateFromUrl(form);

    const sectionId = root.getAttribute("data-section-id");
    const paginationMode = root.getAttribute("data-pagination-mode");
    const mqMobile = window.matchMedia("(max-width: 980px)");
    const filterAside = root.querySelector("[data-collection-filters]");
    const backdrop = root.querySelector("[data-filter-backdrop]");
    const openBtn = root.querySelector("[data-filter-drawer-open]");
    const sortSelect = root.querySelector("[data-collection-sort]");
    const loadMoreBtn = root.querySelector("[data-load-more]");

    let requestInFlight = false;
    let inputDebounce = null;

    const syncShowingCount = () => {
      const showingWrap = root.querySelector("[data-collection-showing]");
      if (!showingWrap) return;
      const startEl = showingWrap.querySelector("[data-collection-range]");
      const endEl = showingWrap.querySelector("[data-collection-range-end]");
      const totalEl = showingWrap.querySelector("[data-collection-total]");
      const grid = root.querySelector("[data-product-grid]");

      const offset = Number.parseInt(showingWrap.dataset.currentOffset || "0", 10) || 0;
      const fallbackSize = Number.parseInt(showingWrap.dataset.pageSize || "0", 10) || 0;
      const total = Number.parseInt(showingWrap.dataset.totalCount || "0", 10) || 0;
      const visibleCount = grid ? grid.querySelectorAll("[data-product-card]").length : fallbackSize;

      const start = visibleCount > 0 ? offset + 1 : 0;
      const end = visibleCount > 0 ? offset + visibleCount : 0;

      if (startEl) startEl.textContent = String(start);
      if (endEl) endEl.textContent = String(end);
      if (totalEl && total > 0) totalEl.textContent = String(total);
    };

    const closeDrawer = () => {
      filterAside?.classList.remove("is-open");
      backdrop?.classList.remove("is-open");
      backdrop?.setAttribute("hidden", "");
      document.body.classList.remove("filter-drawer-open");
      openBtn?.setAttribute("aria-expanded", "false");
      filterAside?.removeAttribute("aria-modal");
    };

    const openDrawer = () => {
      if (!mqMobile.matches) return;
      filterAside?.classList.add("is-open");
      backdrop?.classList.add("is-open");
      backdrop?.removeAttribute("hidden");
      document.body.classList.add("filter-drawer-open");
      openBtn?.setAttribute("aria-expanded", "true");
      filterAside?.setAttribute("aria-modal", "true");
      filterAside?.querySelector("[data-filter-drawer-close]")?.focus({ preventScroll: true });
    };

    const onMq = () => {
      if (!mqMobile.matches) closeDrawer();
    };
    if (typeof mqMobile.addEventListener === "function") mqMobile.addEventListener("change", onMq);
    else if (typeof mqMobile.addListener === "function") mqMobile.addListener(onMq);

    const fetchAndSwap = async (urlLike, opts = {}) => {
      if (requestInFlight) return;
      requestInFlight = true;
      root.classList.add("is-loading");
      const preservedScrollY = window.scrollY;

      const targetUrl = new URL(urlLike, window.location.origin);
      const sectionUrl = urlWithSection(targetUrl, sectionId);

      try {
        const response = await fetch(sectionUrl.toString(), {
          credentials: "same-origin",
          headers: { "X-Requested-With": "XMLHttpRequest" }
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const incomingRoot = parseSectionFromHtml(html);
        if (!incomingRoot) throw new Error("Collection section not found in response");

        revealNow(incomingRoot);
        root.replaceWith(incomingRoot);
        if (opts.pushState) history.pushState({}, "", targetUrl.toString());
        if (opts.replaceState) history.replaceState({}, "", targetUrl.toString());
        initCollection(incomingRoot);
        requestAnimationFrame(() => {
          window.scrollTo({ top: preservedScrollY, left: 0, behavior: "auto" });
        });
      } catch (_err) {
        if (opts.fallback === "submit") form.submit();
        else window.location.href = targetUrl.toString();
      } finally {
        requestInFlight = false;
      }
    };

    const submitViaAjax = (pushState = true) => {
      const nextUrl = buildFormUrl(form);
      nextUrl.searchParams.delete("page");
      closeDrawer();
      fetchAndSwap(nextUrl.toString(), { pushState, fallback: "submit" });
    };

    openBtn?.addEventListener("click", openDrawer);
    root.querySelectorAll("[data-filter-drawer-close]").forEach((button) => {
      button.addEventListener("click", closeDrawer);
    });
    backdrop?.addEventListener("click", closeDrawer);

    form.addEventListener("submit", (event) => {
      event.preventDefault();
      submitViaAjax(true);
    });

    form.querySelectorAll('input[type="checkbox"]').forEach((el) => {
      el.addEventListener("change", () => submitViaAjax(true));
    });

    form.querySelectorAll('input[type="number"]').forEach((el) => {
      el.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        submitViaAjax(true);
      });
      el.addEventListener("change", () => {
        window.clearTimeout(inputDebounce);
        inputDebounce = window.setTimeout(() => submitViaAjax(true), 180);
      });
    });

    sortSelect?.addEventListener("change", () => submitViaAjax(true));

    wirePriceRange(form, () => {
      window.clearTimeout(inputDebounce);
      inputDebounce = window.setTimeout(() => submitViaAjax(true), 180);
    });

    wirePriceBuckets(form, () => submitViaAjax(true));

    root.addEventListener("click", (event) => {
      const anchor = event.target.closest("a[data-collection-ajax-link]");
      if (!anchor || !root.contains(anchor)) return;
      event.preventDefault();
      fetchAndSwap(anchor.href, { pushState: true, fallback: "navigate" });
    });

    if (paginationMode === "load_more" && loadMoreBtn) {
      const grid = root.querySelector("[data-product-grid]");
      if (!grid) return;

      loadMoreBtn.addEventListener("click", async () => {
        const nextUrlRaw = loadMoreBtn.getAttribute("data-next-url");
        if (!nextUrlRaw || requestInFlight) return;

        requestInFlight = true;
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = "Loading...";

        try {
          const sectionUrl = urlWithSection(nextUrlRaw, sectionId);
          const response = await fetch(sectionUrl.toString(), {
            credentials: "same-origin",
            headers: { "X-Requested-With": "XMLHttpRequest" }
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const html = await response.text();
          const incomingRoot = parseSectionFromHtml(html);
          if (!incomingRoot) throw new Error("Missing collection section in response");

          const nextGrid = incomingRoot.querySelector("[data-product-grid]");
          if (!nextGrid) throw new Error("Missing product grid in response");

          nextGrid.querySelectorAll("[data-product-card]").forEach((card) => {
            card.classList.add("is-visible");
            grid.appendChild(card);
          });

          const nextButton = incomingRoot.querySelector("[data-load-more]");
          const upcomingUrl = nextButton?.getAttribute("data-next-url");
          if (upcomingUrl) {
            loadMoreBtn.setAttribute("data-next-url", upcomingUrl);
            loadMoreBtn.disabled = false;
            loadMoreBtn.textContent = "Load more";
          } else {
            loadMoreBtn.remove();
          }

          syncShowingCount();
          history.replaceState({}, "", nextUrlRaw);
        } catch (_err) {
          loadMoreBtn.disabled = false;
          loadMoreBtn.textContent = "Try again";
        } finally {
          requestInFlight = false;
        }
      });
    }

    syncShowingCount();
  };

  const initialRoot = document.querySelector("[data-collection-enhanced]");
  if (initialRoot) initCollection(initialRoot);

  if (!window.__ttCollectionPopstateBound) {
    window.__ttCollectionPopstateBound = true;
    window.addEventListener("popstate", () => {
      if (!isCollectionPage()) return;
      const currentRoot = document.querySelector("[data-collection-enhanced]");
      if (!currentRoot) return;

      const sectionId = currentRoot.getAttribute("data-section-id");
      const sectionUrl = urlWithSection(window.location.href, sectionId);
      fetch(sectionUrl.toString(), {
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const incomingRoot = parseSectionFromHtml(html);
          if (!incomingRoot) throw new Error("Collection section not found");
          revealNow(incomingRoot);
          currentRoot.replaceWith(incomingRoot);
          initCollection(incomingRoot);
        })
        .catch(() => {
          window.location.reload();
        });
    });
  }
})();
